import DOMPurify from 'dompurify';
import React from 'react';

import Dialog from '@material-ui/core/Dialog';
import { makeStyles } from '@material-ui/core/styles';

import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import SimpleModalHeader from 'components/SimpleModalHeader/SimpleModalHeader';

const useStyles = makeStyles((theme) => ({
  buttonContainer: {
    textAlign: 'center',
    margin: '30px 0 0',
  },
  emailSubject: {
    fontSize: '22px',
    fontWeight: 600,
    marginBottom: '50px',
  },
  emailText: {
    fontWeight: 400,
    '& *': {
      whiteSpace: 'normal',
    },
  },
}));

interface Props {
  title: string;
  emailSubject: string | undefined;
  emailHtml: string | undefined;
  onClose: () => void;
}

const SafeHtmlContainer: React.FC<{ html: string }> = ({ html }) => {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const resizeObserverRef = React.useRef<ResizeObserver | null>(null);
  const resizeFrameRef = React.useRef<number | null>(null);
  const [height, setHeight] = React.useState(200);

  const stopObserving = () => {
    resizeObserverRef.current?.disconnect();
    if (resizeFrameRef.current !== null) cancelAnimationFrame(resizeFrameRef.current);
  };

  React.useEffect(() => stopObserving, []);

  const resizeToContent = () => {
    stopObserving();
    const frameWindow = iframeRef.current?.contentWindow;
    const body = iframeRef.current?.contentDocument?.body;
    if (!frameWindow || !body) return;

    const updateHeight = () => {
      if (iframeRef.current?.contentDocument?.body !== body) return;
      const styles = frameWindow.getComputedStyle(body);
      if (!styles) return;
      const margins = parseFloat(styles.marginTop) + parseFloat(styles.marginBottom);
      setHeight(Math.max(200, Math.ceil(body.scrollHeight + margins)));
    };

    updateHeight();
    resizeObserverRef.current = new ResizeObserver(() => {
      // Updating the iframe during observer delivery can cause a resize loop in Firefox.
      if (resizeFrameRef.current !== null) cancelAnimationFrame(resizeFrameRef.current);
      resizeFrameRef.current = requestAnimationFrame(updateHeight);
    });
    resizeObserverRef.current.observe(body);
  };

  return (
    <iframe
      ref={iframeRef}
      title="Email preview"
      srcDoc={html}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      onLoad={resizeToContent}
      style={{
        display: 'block', border: 0, width: '100%', height,
      }}
    />
  );
};

const ViewEmailModal: React.FC<Props> = ({
  title, emailSubject, emailHtml, onClose,
}: Props) => {
  const classes = useStyles();

  const cleanHtml = React.useMemo(() => {
    if (!emailHtml) return null;
    return DOMPurify.sanitize(emailHtml, {
      // Keep head styles (including @font-face) inside the isolated preview document.
      WHOLE_DOCUMENT: true,
      USE_PROFILES: { html: true },
      // Retain MJML's font stylesheets; @import alone can fail in Firefox srcdoc frames.
      ADD_TAGS: ['link'],
      ADD_ATTR: ['target', 'rel'],
    });
  }, [emailHtml]);

  return (
    <Dialog
      open
      scroll="body"
      keepMounted
      fullWidth
      maxWidth="lg"
      onClose={onClose}
    >
      <SimpleModalHeader onClose={onClose} title={title} />

      {emailSubject && cleanHtml
        ? (
          <>
            <div className={classes.emailSubject}>{`Subject: ${emailSubject}`}</div>
            {/* <div className={classes.emailText} dangerouslySetInnerHTML={{ __html: cleanHtml }} /> */}
            <SafeHtmlContainer html={cleanHtml} />
          </>
        )
        : <LoadingIndicator /> }

      {/* <div className={classes.buttonContainer}>
        <Button
          round
          color="primary"
          onClick={onClose}
        >
          Close
        </Button>
      </div> */}
    </Dialog>
  );
};

export default ViewEmailModal;
